import React from "react";

const CancellationPolicy = () => {
  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="bg-red-600 py-12 text-center text-white">
        <h1 className="text-3xl font-bold md:text-4xl">Order Cancellation Policy</h1>
        <p className="mt-3 text-sm md:text-base">For Customers Shopping on Havbit Marketplace</p>
        <div className="mt-4 text-xs md:text-sm opacity-90">
          <p>Last Updated: May 2026 | Effective Date: May 2026 | Version: 1.0</p>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-10 md:py-12">
        <div className="mb-10 rounded-lg border border-gray-200 bg-gray-50 p-6">
          <h2 className="mb-4 text-xl font-semibold text-red-600">Table of Contents</h2>
          <ol className="list-decimal list-inside space-y-2 text-sm md:text-base">
            <li>Why Cancellation Rules Matter for Food Orders</li>
            <li>Customer-Initiated Cancellations</li>
            <li>How to Cancel Your Order</li>
            <li>Cancellations Initiated by the Seller</li>
            <li>Cancellations Initiated by Havbit</li>
            <li>Refund on Cancellation</li>
            <li>Instant Refund to Havbit Wallet</li>
            <li>Cancellation and Your Account Standing</li>
            <li>Checking Your Cancellation and Refund Status</li>
          </ol>
        </div>

        <div className="space-y-8 text-base leading-7 text-gray-800">
          <p>
            This Order Cancellation Policy explains when and how you can cancel an order placed on the
            Havbit marketplace, the circumstances in which an order may be cancelled by the seller or
            by Havbit, and what happens to your payment in each case. Please read this Policy carefully
            before placing an order, particularly if you think you may need to cancel.
          </p>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">1. Why Cancellation Rules Matter for Food Orders</h2>
            <p>
              Havbit is a food marketplace. The vast majority of products sold on our platform are
              perishable food items with limited shelf lives. When you place an order, the seller
              immediately begins preparing and packing your order to meet Havbit's strict 24-hour
              dispatch requirement. Cancelling an order after it has been packed results in food waste,
              financial loss for the seller, and wasted logistics resources. For these reasons, our
              cancellation window is shorter than you may be used to on general e-commerce platforms.
              We appreciate your understanding and encourage you to review your order carefully before
              confirming.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">2. Customer-Initiated Cancellations</h2>

            <h3 className="mb-2 text-lg font-semibold text-gray-900">2.1 The 1-Hour Cancellation Window</h3>
            <p>
              You may cancel your order within 1 hour of placing it, provided the seller has not yet
              confirmed and started processing the order. This 1-hour window is referred to as the
              Customer Cancellation Window. During this window, cancellation is straightforward,
              immediate, and results in a full refund. The 1-hour cancellation window begins from the
              exact time your order is confirmed — that is, when you receive the order confirmation
              notification from Havbit. To cancel within this window, go to 'My Orders' in your Havbit
              app or account on the website, select the order, and tap or click 'Cancel Order'. Follow the
              prompts to confirm the cancellation.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">2.2 What Happens When the Order Is Already Confirmed by the Seller</h3>
            <p>
              Once a seller marks your order as 'Accepted' or 'Processing' in their Seller Dashboard —
              which may happen within minutes of you placing the order if the seller is active — the
              Customer Cancellation Window closes early, even if 1 hour has not passed. Once the order is
              in processing, cancellation is no longer available through the standard cancellation feature.
            </p>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">2.3 Cancellation After the 1-Hour Window</h3>
            <p>
              After the 1-hour Customer Cancellation Window closes, standard cancellation is not
              available for food products on Havbit. If you still wish to cancel your order after this point,
              the only available remedies are:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Refuse the delivery: When the courier arrives with your order, you may refuse to accept it. The order will be marked as 'Customer Refused' and returned to the seller. A refund will be processed after deduction of forward and return shipping charges</li>
              <li>Return after delivery: If you accept the delivery and then find a valid return reason — such as damage, wrong product, expiry, or quality mismatch — you may raise a return request within 48 hours of delivery under our Return and Refund Policy</li>
              <li>Contact support: If there is a specific circumstance — such as a listing error or product safety concern — that justifies cancellation, contact us at hello@havbit.in and we will review your case individually</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">2.4 Exceptions — When We Accept Late Cancellations</h3>
            <p>
              Havbit may accept a cancellation request after the standard window in the following exceptional circumstances:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>The product listing on the platform contained a material error — such as a significantly incorrect price, wrong product image, or missing allergen information that was brought to Havbit's attention</li>
              <li>The seller has confirmed that the product is out of stock and cannot fulfil the order</li>
              <li>A food safety alert or product recall has been issued for the specific product after your order was placed</li>
              <li>A technical error on the Havbit platform resulted in duplicate orders being placed</li>
              <li>The delivery address entered during checkout cannot be serviced, and this was not disclosed before order placement</li>
            </ul>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">3. How to Cancel Your Order</h2>
            <p>Follow these steps to cancel an order within the permitted cancellation window:</p>
            <ul className="mt-3 list-decimal space-y-2 pl-6">
              <li>Open the Havbit app or log in to www.havbit.in</li>
              <li>Tap or click 'My Orders' in the main navigation menu or home screen</li>
              <li>Find the order you wish to cancel — it will be listed under 'Active Orders' or 'Recent Orders'</li>
              <li>Tap or click on the order to open the order details page</li>
              <li>If the order is within the cancellation window, you will see a 'Cancel Order' button. Tap or click it</li>
              <li>Select the reason for cancellation from the dropdown menu — this helps us improve the platform</li>
              <li>Confirm the cancellation. You will receive an immediate cancellation confirmation via SMS and email</li>
            </ul>
            <p className="mt-4">
              If you do not see the 'Cancel Order' button on the order details page, the cancellation window has closed or the order has already been processed by the seller. In this case, please refer to Section 2.3 above for your options.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">4. Cancellations Initiated by the Seller</h2>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">4.1 When Sellers Cancel Orders</h3>
            <p>
              In some cases, a seller may need to cancel your order after it has been confirmed. This is generally an uncommon occurrence as sellers are required to maintain accurate inventory levels. Seller cancellations may happen in the following situations:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>The product you ordered is out of stock and the seller is unable to fulfil the order within the dispatch SLA</li>
              <li>The seller has identified that the specific batch of the product has a quality or safety issue that prevents dispatch</li>
              <li>An exceptional circumstance — such as a natural disaster, power failure, or other force majeure event — prevents the seller from fulfilling the order</li>
              <li>The delivery address provided is incomplete, incorrect, or unserviceable, and the customer cannot be reached to provide a correction</li>
            </ul>

            <h3 className="mt-5 mb-2 text-lg font-semibold text-gray-900">4.2 Your Rights When a Seller Cancels</h3>
            <p>
              If a seller cancels your order, you will be notified by Havbit immediately via SMS, email, and app notification. You are entitled to a full refund of the order value, including any shipping charges paid, with no deductions. The refund will be processed to your original payment method within the standard refund timelines. Sellers who cancel orders are subject to performance consequences under Havbit's seller policies, including impact on their seller rating and potential listing restrictions.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">5. Cancellations Initiated by Havbit</h2>
            <p>
              Havbit's operations or compliance team may initiate the cancellation of your order in the following circumstances:
            </p>
            <ul className="mt-3 list-disc space-y-2 pl-6">
              <li>Payment failure: A prepaid transaction that was initially authorised is subsequently reversed, declined, or flagged as fraudulent by the payment gateway</li>
              <li>Prohibited product: The product is identified as prohibited under Havbit's policies or Indian food safety regulations after your order was placed</li>
              <li>Product recall: FSSAI or the seller initiates a product recall for the specific batch that covers your order</li>
              <li>Fraud detection: Your order is flagged by Havbit's fraud detection systems as potentially fraudulent and cannot be cleared within the processing timeline</li>
              <li>Unserviceable address: Post-order verification reveals that the delivery address is unserviceable by all available courier partners</li>
              <li>Legal or regulatory order: A court order, regulatory direction, or law enforcement requirement mandates the cancellation of the order</li>
            </ul>
            <p className="mt-4">
              In all cases of Havbit-initiated cancellations, a full refund will be processed to your original payment method within the standard refund timeline. You will be notified of the cancellation and its reason as soon as possible. Havbit-initiated cancellations do not reflect negatively on your account.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">6. Refund on Cancellation</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 text-left text-sm">
                <thead className="bg-red-50 text-gray-900">
                  <tr>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Payment Method</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Refund Timeline</th>
                    <th className="border border-gray-200 px-3 py-2 font-semibold">Refund Destination</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">UPI — GPay, PhonePe, Paytm, etc.</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Original UPI account / bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Net Banking</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Source bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Credit Card</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Credit card statement credit</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Debit Card</td>
                    <td className="border border-gray-200 px-3 py-2">3–5 business days</td>
                    <td className="border border-gray-200 px-3 py-2">Source bank account</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Havbit Wallet</td>
                    <td className="border border-gray-200 px-3 py-2">Within 24 hours</td>
                    <td className="border border-gray-200 px-3 py-2">Your Havbit Wallet balance</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">Cash on Delivery</td>
                    <td className="border border-gray-200 px-3 py-2">No refund needed</td>
                    <td className="border border-gray-200 px-3 py-2">No charge collected — order not delivered</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-3 py-2">No-Cost EMI</td>
                    <td className="border border-gray-200 px-3 py-2">5–7 business days</td>
                    <td className="border border-gray-200 px-3 py-2">EMI cancelled — card credited</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4">
              Refund timelines start from the date the cancellation is confirmed and the refund is initiated by Havbit, not from the date you requested the cancellation. The actual credit to your account depends on your bank's or payment gateway's internal processing timelines, which may occasionally extend beyond the stated periods.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">7. Instant Refund to Havbit Wallet</h2>
            <p>
              For faster resolution, Havbit offers an instant refund option to your Havbit Wallet for eligible cancellations. If you choose this option at the time of cancellation, your refund will be credited to your Havbit Wallet within a few hours, allowing you to immediately use the balance for another purchase. Wallet refunds are optional — the default is a refund to your original payment method.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">8. Cancellation and Your Account Standing</h2>
            <p>
              Customer-initiated cancellations within the permitted window, seller-initiated cancellations, and Havbit-initiated cancellations have no negative impact on your customer account standing. However, Havbit monitors accounts for patterns of behaviour that may indicate platform abuse — such as repeatedly placing and cancelling large orders, consistently refusing COD deliveries, or filing multiple false return claims after delivery. Accounts exhibiting such patterns may be subject to restrictions, including disabling of the COD payment option or temporary account review.
            </p>
          </section>

          <section>
            <h2 className="mb-3 text-2xl font-semibold text-red-600">9. Checking Your Cancellation and Refund Status</h2>
            <ul className="list-disc space-y-2 pl-6">
              <li>Havbit App / Website: Go to 'My Orders' → select the cancelled order → view the cancellation confirmation and refund status</li>
              <li>Email and SMS: You will receive automatic notifications confirming the cancellation and refund initiation</li>
              <li>Customer Support: Email hello@havbit.in with your Order ID if you have not received your refund within the stated timeline</li>
            </ul>
          </section>

          <div className="border-t border-gray-200 pt-8 text-sm text-gray-700">
            <p className="font-semibold text-gray-900">Havbit Global Private Limited</p>
            <p>CIN: U63120HR2026PTC141507</p>
            <p>Innov8 UCP, 9th Floor, Tower D, Unitech Cyber Park, Gurgaon – 122001, Haryana, India</p>
            <p>Customer Support Email: hello@havbit.in</p>
            <p>Website: www.havbit.in</p>
            <p>Support Hours: Monday – Saturday, 10:00 AM – 6:00 PM IST</p>
            <p className="mt-4">
              By using the Havbit platform — whether by browsing, creating an account, or placing an order — you confirm that you have read, understood, and agree to be bound by this Order Cancellation Policy and all related Havbit policies as updated from time to time.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-6 text-center text-xs font-medium text-gray-600">
            © 2026 Havbit Global Private Limited | www.havbit.in | hello@havbit.in
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;
